import React, { useEffect, useState } from 'react';
import { CheckCircle, Download, ArrowLeft } from 'lucide-react';
import { useMarkingStore } from '../../stores/markingStore';
import ScoreSummaryTable from './ScoreSummaryTable';
import MarkedPdfViewer from './MarkedPdfViewer';
import { generateMarkedImageBlob } from '../../utils/manualMarkingImage';

const safeFileName = (value) => String(value || 'marked-script')
  .trim()
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'marked-script';

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const MarkingResultsView = ({ jobId, onBack }) => {
  const { currentJob, loadMarkingJob, getMarkedPdfUrl } = useMarkingStore();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [exportingFormat, setExportingFormat] = useState('');
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    if (jobId && (!currentJob || currentJob.id !== jobId)) {
      loadMarkingJob(jobId);
    }
  }, [jobId, currentJob, loadMarkingJob]);

  useEffect(() => {
    if (currentJob?.markedPdfPath) {
      getMarkedPdfUrl(currentJob.markedPdfPath)
        .then(setPdfUrl)
        .catch(console.error);
    }
  }, [currentJob?.markedPdfPath, getMarkedPdfUrl]);

  const scriptFiles = Array.isArray(currentJob?.scriptFiles) ? currentJob.scriptFiles : [];
  const firstScript = scriptFiles[0];
  const isImageSource = Boolean(
    firstScript &&
    !(String(firstScript.mimeType || '').toLowerCase().includes('pdf') || /\.pdf$/i.test(firstScript.path || ''))
  );

  const handlePdfExport = async () => {
    if (!pdfUrl) return;
    setExportingFormat('pdf');
    setExportError('');
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error(`PDF download failed (${response.status}).`);
      downloadBlob(await response.blob(), `${safeFileName(currentJob?.title)}.pdf`);
    } catch (error) {
      console.error('Failed to export marked PDF:', error);
      setExportError(error?.message || 'Failed to export the marked PDF.');
    } finally {
      setExportingFormat('');
    }
  };

  const handleImageExport = async (format) => {
    if (!scriptFiles.length) return;
    setExportingFormat(format);
    setExportError('');
    try {
      for (let index = 0; index < scriptFiles.length; index += 1) {
        const entry = scriptFiles[index];
        if (!entry?.path) throw new Error(`Script image ${index + 1} has no storage path.`);
        const sourceUrl = await getMarkedPdfUrl(entry.path);
        const blob = await generateMarkedImageBlob({
          sourceUrl,
          results: currentJob?.results,
          pageNumber: index + 1,
          format
        });
        const pageSuffix = scriptFiles.length > 1 ? `-page-${index + 1}` : '';
        const extension = format === 'jpeg' ? 'jpg' : 'png';
        downloadBlob(blob, `${safeFileName(currentJob?.title)}${pageSuffix}.${extension}`);
      }
    } catch (error) {
      console.error(`Failed to export marked ${format}:`, error);
      setExportError(error?.message || `Failed to export the marked ${format}.`);
    } finally {
      setExportingFormat('');
    }
  };

  if (!currentJob) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  const results = currentJob.results;
  const isDirectAi = results?.renderMode === 'ai-direct';
  const percentage = results?.percentage || 0;
  const scoreColor = percentage >= 70 ? 'text-green-600' : percentage >= 50 ? 'text-yellow-600' : 'text-red-600';
  const scoreBg = percentage >= 70 ? 'bg-green-50 border-green-200' : percentage >= 50 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {onBack && (
            <button onClick={onBack} className="p-1 text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              {currentJob.title || 'Marking Results'}
            </h2>
            <p className="text-sm text-gray-500">
              {currentJob.subject && `${currentJob.subject} `}
              {currentJob.grade && `Grade ${currentJob.grade} `}
              {currentJob.learnerName && `- ${currentJob.learnerName}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isImageSource ? (
            <>
              <button
                type="button"
                onClick={() => handleImageExport('png')}
                disabled={Boolean(exportingFormat)}
                className="inline-flex items-center px-3 py-2 bg-sigma-600 text-white text-sm font-medium rounded-md hover:bg-sigma-700 disabled:opacity-60"
              >
                <Download className="w-4 h-4 mr-1" />
                {exportingFormat === 'png' ? 'Exporting...' : 'Download PNG'}
              </button>
              <button
                type="button"
                onClick={() => handleImageExport('jpeg')}
                disabled={Boolean(exportingFormat)}
                className="inline-flex items-center px-3 py-2 bg-sigma-600 text-white text-sm font-medium rounded-md hover:bg-sigma-700 disabled:opacity-60"
              >
                <Download className="w-4 h-4 mr-1" />
                {exportingFormat === 'jpeg' ? 'Exporting...' : 'Download JPEG'}
              </button>
            </>
          ) : pdfUrl ? (
            <button
              type="button"
              onClick={handlePdfExport}
              disabled={Boolean(exportingFormat)}
              className="inline-flex items-center px-3 py-2 bg-sigma-600 text-white text-sm font-medium rounded-md hover:bg-sigma-700 disabled:opacity-60"
            >
              <Download className="w-4 h-4 mr-1" />
              {exportingFormat === 'pdf' ? 'Exporting...' : 'Download Marked PDF'}
            </button>
          ) : null}
        </div>
      </div>

      {exportError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {exportError}
        </div>
      )}

      {/* Score summary cards */}
      {results && !isDirectAi && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`rounded-lg border p-4 ${scoreBg}`}>
            <p className="text-sm text-gray-600 mb-1">Total Score</p>
            <p className={`text-3xl font-bold ${scoreColor}`}>
              {results.totalScore}/{results.maxScore}
            </p>
          </div>
          <div className={`rounded-lg border p-4 ${scoreBg}`}>
            <p className="text-sm text-gray-600 mb-1">Percentage</p>
            <p className={`text-3xl font-bold ${scoreColor}`}>
              {results.percentage}%
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-600 mb-1">Pages Marked</p>
            <p className="text-3xl font-bold text-gray-900">
              {results.pageResults?.length || 0}
            </p>
          </div>
        </div>
      )}

      {isDirectAi && (
        <div className="rounded-lg border border-sigma-200 bg-sigma-50 p-4 text-sm text-sigma-900">
          This script was marked directly by the AI model and returned as a completed marked document.
        </div>
      )}

      {/* Score summary table */}
      {results?.pageResults?.length > 0 && (
        <ScoreSummaryTable pageResults={results.pageResults} />
      )}

      {/* Marked PDF viewer */}
      {pdfUrl && !isImageSource && <MarkedPdfViewer url={pdfUrl} />}
    </div>
  );
};

export default MarkingResultsView;
